"use client";

import { useEffect, useMemo } from "react";
import { useStatsigClient } from "@statsig/react-bindings";

/**
 * Slow-changing user state that should be set on the Statsig user `custom`
 * field, not stamped onto every event. See `packages/statsig/TAXONOMY.md`
 * → "User properties vs event metadata".
 *
 * Per the v2 taxonomy:
 * - `brand`: kinsa | owlet (build-time constant)
 * - `subscription_status`: paying | trialing | past_due | canceled_but_active | canceled | none
 *   (also encodes pre-signup vs post-signup vs ever-paid via the value: "none"
 *    means no subscription known; everything else implies progressively deeper
 *    funnel state)
 * - `current_product_key`: raw Stripe lookup key for the active subscription
 * - `current_billing_period`: monthly | annual
 * - `current_trial_days`: trial length variant the user is in
 * - `n_children`: count of eligible covered persons
 * - `n_visits_lifetime` / `n_visits_30d`: from Prisma denormalization
 * - `account_age_days`: derived from account.createdAt
 * - `is_internal`: true for Felt/Owlet staff & test accounts (by email
 *   domain). Lets current-state metrics that group by the user object filter
 *   out internal traffic the way event-based metrics do via `customer_email`.
 */
export interface StatsigUserCustomProps {
  brand?: string;
  /** Raw Stripe subscription status (see @repo/stripe normalizeSubscriptionStatus). */
  subscription_status?: string;
  /** Whether the subscription is scheduled to cancel at period end — orthogonal to status. */
  cancels_at_period_end?: boolean;
  current_product_key?: string | null;
  current_billing_period?: string | null;
  current_trial_days?: number | null;
  n_children?: number;
  n_visits_lifetime?: number;
  n_visits_30d?: number;
  account_age_days?: number;
  is_internal?: boolean;
}

/**
 * Sync `props` into the Statsig user `custom` field. Re-runs when any value
 * changes. Preserves any other custom fields and customIDs already set.
 *
 * Mount alongside `<StatsigObserver />` in your providers tree, with a
 * thin app-specific component that pulls these values from your context
 * (account, subscription state, env) and passes them in.
 */
export function useStatsigUserProperties(props: StatsigUserCustomProps): void {
  const { client } = useStatsigClient();

  const custom = useMemo<Record<string, string | number | boolean>>(() => {
    const result: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(props)) {
      if (value === undefined || value === null) continue;
      result[key] = value as string | number | boolean;
    }
    return result;
  }, [props]);

  // Stable JSON key so we don't re-update on referentially-different but
  // equal objects (common when props are reconstructed every render).
  const customKey = useMemo(() => JSON.stringify(custom), [custom]);

  useEffect(() => {
    if (!client) return;
    const currentUser = client.getContext().user;
    client.updateUserSync({
      ...currentUser,
      custom: { ...currentUser?.custom, ...custom },
    });
    // customKey ensures we only fire when values actually change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, customKey]);
}
